/**
 * The colour maths behind the tenant brand.
 *
 * Pure and framework-free so it can be tested directly: these three functions decide whether text
 * on a tenant's chosen colour is readable, and that is not something to find out one tenant at a
 * time. Lives in `shared/` rather than next to the hook for the same reason the schemas do.
 */

export const BRAND_HEX = /^#[0-9a-fA-F]{6}$/;

/** The 8-bit channels of a `#rrggbb` string. */
function channels(hex: string): [number, number, number] {
  return [0, 1, 2].map((i) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16)) as
    [number, number, number];
}

/**
 * WCAG relative luminance, 0 (black) to 1 (white).
 *
 * The green coefficient dominates on purpose — that is the standard, and it is why a saturated
 * yellow needs dark text while a saturated blue of the same "brightness" needs light text.
 */
export function luminance(hex: string) {
  const [r, g, b] = channels(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** `hex` moved `amount` (0–1) of the way toward white. */
export function lighten(hex: string, amount: number) {
  const parts = channels(hex)
    .map((v) => Math.round(v + (255 - v) * amount).toString(16).padStart(2, "0"));
  return `#${parts.join("")}`;
}

/** `hex` moved `amount` (0–1) of the way toward black. */
export function darken(hex: string, amount: number) {
  const parts = channels(hex)
    .map((v) => Math.round(v * (1 - amount)).toString(16).padStart(2, "0"));
  return `#${parts.join("")}`;
}

/** WCAG contrast ratio between two hex colours, 1 (identical) to 21 (black on white). */
export function contrast(a: string, b: string) {
  const [x, y] = [luminance(a), luminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/**
 * Readable text on a swatch of `colour`.
 *
 * The two candidates are white and a very dark tint of the colour itself. Dark rather than flat
 * black because the hand-picked default is: `--primary-foreground` is #00231E on #00BFA5, a near
 * black carrying the hue, and 80% toward black reproduces that within a shade.
 *
 * It measures both and returns the better one rather than testing luminance against a threshold.
 * A threshold is where this goes wrong quietly: the first cut used 0.42, the default turquoise
 * measures 0.40, so the app's own brand colour fell on the "use white" side — and white on
 * #00BFA5 is 2.33:1, the exact combination docs/06-design-system.md rejects by name. Comparing
 * the candidates cannot develop that kind of off-by-a-hair bug.
 */
export function onBrandColour(colour: string) {
  if (!BRAND_HEX.test(colour)) return "var(--primary-foreground)";
  const tint = darken(colour, 0.8);
  return contrast(colour, tint) >= contrast(colour, "#ffffff") ? tint : "#ffffff";
}
